import { useSWRConfig } from 'swr'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from '../../../../lib/i18n'
import BaseModal from '../../../common/modal/BaseModal'
import CLAvatar from '../../../common/CLAvatar'
import Button from '../../../common/Button'
import useGroup from '../../../../lib/useGroup'
import { useCacheKey } from '../../../context/CacheKey'
import TextInput from '../../../common/TextInput'
import applicationFormSchema from './schemas/applicationForm'
import useUser from '../../../../lib/useUser'

const ApplicationForm = ({ question, onSubmit, control, errors }) => {
  const { t } = useTranslation()

  return (
    <form onSubmit={onSubmit} className="w-full mt-3">
      <div className="text-sm italic">{question}</div>
      <Controller
        control={control}
        name="applicationAnswer"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextInput
            name="applicationAnswer"
            placeholder={t('forms.application.answerPlaceholder')}
            multi
            value={value}
            hint={error?.message}
            isError={!!error?.message}
            charLimit={240}
            onChangeText={onChange}
          />
        )}
      />
      <Button primary type="submit" className="mt-4">
        {t('ui.actions.confirm')}
      </Button>
    </form>
  )
}

const JoinConfirmModal = ({
  groupSlug,
  isOpen,
  onRequestClose,
  invite,
  onJoin,
}) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { group, createGroupMembership } = useGroup(groupSlug, { invite })
  const { mutate } = useSWRConfig()
  const cacheKey = useCacheKey()

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(applicationFormSchema),
    defaultValues: {
      applicationAnswer: '',
    },
  })

  const handleJoinGroup = async (data) => {
    try {
      const resp = await createGroupMembership(user?.id, 'applied', data)
      mutate(
        invite
          ? `/api/group_invite/${invite}`
          : `/api/groups/${groupSlug}.json`,
        (oldData) => ({
          group: { ...oldData?.group, ...resp?.group },
        }),
        false
      )
      mutate(cacheKey.value)
      onRequestClose()
      reset()
      if (onJoin) {
        onJoin()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onSubmit = (data) => {
    if (group?.application_question) {
      handleJoinGroup({ response: data.applicationAnswer })
    } else {
      handleJoinGroup()
    }
  }

  return (
    <BaseModal isOpen={isOpen} onRequestClose={onRequestClose} closeIcon>
      <div className="max-w-md mx-auto flex flex-col items-center justify-center text-center gap-2">
        <CLAvatar
          avatar={group?.avatar}
          name={group?.name}
          size={96}
          className="bg-gray-100"
        />
        <p className="text-lg font-bold">
          {t('forms.application.applyToJoin', { groupName: group?.name })}
        </p>
        <div className="text-sm">
          <p>
            {t('forms.application.applyingToJoin', { groupName: group?.name })}
          </p>
          <p>{t('forms.application.withdrawInstructions')}</p>
        </div>
        {group?.application_question ? (
          <ApplicationForm
            question={group?.application_question}
            onSubmit={handleSubmit(onSubmit)}
            control={control}
            errors={errors}
          />
        ) : (
          <Button primary onClick={onSubmit}>
            {t('ui.actions.apply')}
          </Button>
        )}
      </div>
    </BaseModal>
  )
}

export default JoinConfirmModal
